import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import Layout from '../../components/Layout';
import LoadingState from '../../components/LoadingState';
import EmptyState from '../../components/EmptyState';
import ErrorBanner from '../../components/ErrorBanner';
import { FolderOpen, MapPin, FolderPlus, Building2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserProjects = () => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setError(null);
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects', error);
      setError(error.response?.data?.detail || t('projects.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <LoadingState message={t('projects.loading')} />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('projects.title')}</h1>
          <p className="text-slate-600 mt-1">{t('projects.subtitle')}</p>
        </div>

        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

        {projects.length === 0 ? (
          <EmptyState 
            icon={FolderPlus}
            title={t('projects.noProjects')}
            description={t('projects.noProjectsDesc')}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <div key={project.id} className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/app/projects/${project.id}/uploads`)} data-testid="project-card">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <FolderOpen className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{project.name}</h3>
                <p className="text-sm text-slate-600 mb-3 line-clamp-2">{project.description || t('projects.noDescription')}</p>
                {project.location && (
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                    <MapPin className="w-4 h-4" />
                    <span>{project.location}</span>
                  </div>
                )}
                <button onClick={(e) => { e.stopPropagation(); navigate(`/app/projects/${project.id}/workspace`); }} className="w-full mb-2 px-3 py-2.5 bg-gradient-to-r from-slate-900 to-indigo-800 text-white text-sm font-medium rounded-lg hover:from-slate-800 hover:to-indigo-700 transition-colors flex items-center justify-center gap-2" data-testid="project-open-workspace-btn"><FolderOpen className="w-4 h-4" /> Open Workspace</button>
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/app/projects/${project.id}/uploads`); }} className="px-2 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors">{t('projects.btnUploads')}</button>
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/app/projects/${project.id}/object-intel`); }} className="px-2 py-2 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition-colors">{t('projects.btnObjectIntel')}</button>
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/app/projects/${project.id}/measure`); }} className="px-2 py-2 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700 transition-colors">{t('projects.btnMeasure2d')}</button>
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/app/projects/${project.id}/3d`); }} className="px-2 py-2 bg-orange-600 text-white text-xs rounded-lg hover:bg-orange-700 transition-colors">{t('projects.btnSurface3d')}</button>
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/app/projects/${project.id}/reference-drawing`); }} className="px-2 py-2 bg-teal-600 text-white text-xs rounded-lg hover:bg-teal-700 transition-colors">{t('projects.btnDrawing')}</button>
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/app/projects/${project.id}/issues`); }} className="px-2 py-2 bg-amber-600 text-white text-xs rounded-lg hover:bg-amber-700 transition-colors" data-testid="project-issues-btn">{t('projects.btnPunchList')}</button>
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/app/projects/${project.id}/cost-codes`); }} className="px-2 py-2 bg-emerald-600 text-white text-xs rounded-lg hover:bg-emerald-700 transition-colors" data-testid="project-costcodes-btn">{t('projects.btnCostCodes')}</button>
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/app/projects/${project.id}/reports`); }} className="px-2 py-2 bg-slate-600 text-white text-xs rounded-lg hover:bg-slate-700 transition-colors">{t('projects.btnReports')}</button>
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/app/projects/${project.id}/history`); }} className="px-2 py-2 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700 transition-colors" data-testid="project-history-btn">{t('projects.btnHistory')}</button>
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/app/projects/${project.id}/boq`); }} className="px-2 py-2 bg-blue-700 text-white text-xs rounded-lg hover:bg-blue-800 transition-colors" data-testid="project-boq-btn">BOQ Intelligence</button>
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/app/projects/${project.id}/schedule`); }} className="px-2 py-2 bg-sky-700 text-white text-xs rounded-lg hover:bg-sky-800 transition-colors" data-testid="project-schedule-btn">Schedule Intelligence</button>
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/app/projects/${project.id}/progress`); }} className="px-2 py-2 bg-indigo-700 text-white text-xs rounded-lg hover:bg-indigo-800 transition-colors" data-testid="project-progress-btn">Progress Intelligence</button>
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/app/projects/${project.id}/design`); }} className="px-2 py-2 bg-teal-700 text-white text-xs rounded-lg hover:bg-teal-800 transition-colors" data-testid="project-design-btn">Design Intelligence</button>
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/app/projects/${project.id}/quality`); }} className="px-2 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors" data-testid="project-quality-btn">Quality Intelligence</button>
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/app/projects/${project.id}/safety`); }} className="px-2 py-2 bg-rose-600 text-white text-xs rounded-lg hover:bg-rose-700 transition-colors" data-testid="project-safety-btn">Safety Intelligence</button>
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/app/projects/${project.id}/site-walks`); }} className="px-2 py-2 bg-sky-600 text-white text-xs rounded-lg hover:bg-sky-700 transition-colors" data-testid="project-sitewalks-btn">360° Site Walks</button>
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/app/projects/${project.id}/controls`); }} className="px-2 py-2 bg-cyan-600 text-white text-xs rounded-lg hover:bg-cyan-700 transition-colors" data-testid="project-controls-btn">Construction Controls</button>
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/app/projects/${project.id}/safety-controls`); }} className="px-2 py-2 bg-orange-600 text-white text-xs rounded-lg hover:bg-orange-700 transition-colors" data-testid="project-safety-btn">Safety Controls</button>
                  {user?.role === 'CompanyAdmin' && (
                    <button onClick={(e) => { e.stopPropagation(); navigate(`/app/projects/${project.id}/construction`); }} className="px-2 py-2 bg-cyan-700 text-white text-xs rounded-lg hover:bg-cyan-800 transition-colors flex items-center justify-center gap-1 col-span-2" data-testid="project-construction-btn"><Building2 className="w-3.5 h-3.5" /> Construction Structure</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default UserProjects;
